import { useState } from "react";
import emailjs from "@emailjs/browser";
import { useAuth } from "../../context/AuthContext"; // Ajusta el path si es necesario
import { doc, getDoc, setDoc, updateDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";

const faqs = [
  {
    question: "¿Cómo cambio mi contraseña?",
    answer: "Ve a tu perfil (ícono de avatar en la esquina superior derecha) → Configuración → sección 'Contraseña' → haz clic en 'Cambiar'. Necesitarás ingresar tu contraseña actual y la nueva. Si no recuerdas tu contraseña actual, primero cierra sesión y usa la opción 'He olvidado mi contraseña' en la pantalla de inicio."
  },
  {
    question: "¿Cómo actualizar mi perfil?",
    answer: "Desde tu modal de perfil puedes editar tu apodo, foto de perfil y biografía."
  },
  {
    question: "¿Cómo puedo recuperar mi contraseña si la olvido?",
    answer: "En la pantalla de inicio de sesión, haz clic en 'He olvidado mi contraseña'. Ingresa tu correo registrado y recibirás un email con un enlace para restablecerla. Revisa también tu carpeta de spam si no lo ves en unos minutos."
  },
  {
    question: "¿Cuánto tarda la verificación de mi cuenta?",
    answer: "El proceso de verificación toma entre 1 y 6 horas en días hábiles. Una vez aprobada, recibirás una notificación en la plataforma y tendrás acceso completo a todas las funciones."
  },
  {
    question: "¿Qué pasa si olvidé mi correo registrado?",
    answer: "Contacta a soporte desde el formulario de abajo o escribe a soporte@sit-app.com indicando tu nombre completo y carrera para que podamos ayudarte a ubicar tu cuenta."
  },
  {
    question: "¿Cómo elimino mi cuenta?",
    answer: "La eliminación de cuenta debe solicitarse a través del soporte. Escríbenos al formulario de abajo o a soporte@sit-app.com con el asunto 'Eliminar mi cuenta'."
  },
  {
    question: "¿Puedo ver mis actividades pasadas?",
    answer: "Por ahora esta función no está disponible, pero está planificada para futuras versiones de SIT."
  },
  {
    question: "¿Qué hacer si mi cuenta fue deshabilitada o rechazada?",
    answer: "Si tu cuenta fue rechazada, aparecerá un mensaje explicativo al iniciar sesión. Puedes contactarnos en soporte@sit-app.com para revisar tu caso. Si crees que fue un error, indícanos tu nombre y carrera."
  },
  {
    question: "¿Cómo reporto un error o problema en la plataforma?",
    answer: "Usa el formulario de soporte al final de esta sección. Describe el problema con el mayor detalle posible (pantalla donde ocurre, mensaje de error si hay alguno). También puedes escribir directamente al chat de soporte."
  },
  {
    question: "¿Qué navegador debo usar?",
    answer: "Chrome, Firefox o Edge actualizados a su última versión. Para la mejor experiencia, recomendamos Google Chrome."
  },
  {
    question: "¿El sitio funciona en móviles?",
    answer: "Sí, SIT es responsive y funciona en móviles. Sin embargo, recomendamos la vista de escritorio para una experiencia completa, especialmente en el área de chat y perfiles promocionales."
  },
  {
    question: "¿Cómo cambio mi correo electrónico asociado a la cuenta?",
    answer: "El cambio de correo requiere verificación adicional por seguridad. Contacta a soporte en soporte@sit-app.com con el asunto 'Cambio de correo' indicando tu correo actual y el nuevo."
  },
  {
    question: "¿Puedo usar la cuenta en varios dispositivos al mismo tiempo?",
    answer: "Sí, puedes acceder desde varios dispositivos simultáneamente. Recuerda cerrar sesión en dispositivos que no uses para mayor seguridad."
  },
  {
    question: "¿Qué hago si detecto un uso no autorizado en mi cuenta?",
    answer: "Cambia tu contraseña de inmediato desde Configuración → Contraseña → Cambiar. Luego contáctanos en soporte@sit-app.com para que podamos revisar la actividad de tu cuenta y tomar medidas adicionales si es necesario."
  }
];


export default function Ayuda() {
  const { currentUser } = useAuth(); // 🔹 Acceso a usuario
  const [expanded, setExpanded] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [enviado, setEnviado] = useState(false);

  const handleSend = async (e) => {
  e.preventDefault();
  if (!mensaje.trim()) return;

  setSending(true);
  setError(null);
  setEnviado(false);

  const templateParams = {
    from_name: currentUser?.displayName || "Consulta anónima",
    from_email: currentUser?.email || "No disponible",
    uid: currentUser?.uid || "N/A",
    message: mensaje,
    timestamp: new Date().toLocaleString(),
  };

  try {
    // 1️⃣ Enviar email
    await emailjs.send(
      "service_j6oh06q",
      "template_3zse07r",
      templateParams,
      "XOmw_cGT7bDjXcB6e"
    );

    // 2️⃣ Guardar notificación en Firestore
    const notificationsRef = doc(db, "NotificationsAdminHistory", "ConsultasUsuarios");
    const newNotification = {
      title: "Nueva consulta de usuario",
      message: `${templateParams.from_name} envió una consulta.`,
      timestamp: Timestamp.now(),
      type: "consulta_usuario",
      read: false,
      details: templateParams,
    };

    const docSnap = await getDoc(notificationsRef);
    if (docSnap.exists()) {
      await updateDoc(notificationsRef, {
        notifications: arrayUnion(newNotification),
      });
    } else {
      await setDoc(notificationsRef, { notifications: [newNotification] });
    }

    // 3️⃣ Reset y feedback
    setEnviado(true);
    setMensaje("");
  } catch (err) {
    console.error("Error al enviar la consulta o guardar la notificación:", err);
    setError("Hubo un problema al enviar tu consulta. Intenta de nuevo.");
  } finally {
    setSending(false);
  }
};

  return (
    <div className="mt-4 text-sm text-gray-700">
      <h2 className="text-lg font-semibold mb-4">Preguntas frecuentes</h2>
      <div className="space-y-2">
        {faqs.map((faq, idx) => (
          <div key={idx} className="border rounded-md p-3">
            <button
              className="w-full text-left font-medium"
              onClick={() => setExpanded(expanded === idx ? null : idx)}
              type="button"
            >
              {faq.question}
            </button>
            {expanded === idx && (
              <p className="mt-2 text-gray-600">{faq.answer}</p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6">
        <h3 className="font-semibold text-base mb-2">¿No encontraste tu respuesta?</h3>
        <form onSubmit={handleSend} className="space-y-2">
          <textarea
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            placeholder="Escribe tu consulta aquí..."
            className="w-full border rounded p-2"
            rows={3}
            required
            disabled={sending}
          />
          {error && <p className="text-red-500">{error}</p>}
          {enviado && <p className="text-green-600">Consulta enviada correctamente ✅</p>}
          <button
            type="submit"
            disabled={sending || !mensaje.trim()}
            className={`px-4 py-2 rounded text-white ${
              mensaje.trim() && !sending ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-300 cursor-not-allowed"
            }`}
          >
            {sending ? "Enviando..." : "Enviar consulta"}
          </button>
        </form>
      </div>
    </div>
  );
}
